import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { useDebouncedValue } from 'hooks';
import { dateToISOStringKey, normalizeDate } from 'utilities/dateUtils';
import { displayErrorMessage } from 'utilities/displayErrorMessage';

type UserRole = 'preparer' | 'reviewer';

// Helper function to sort and merge users with selected users
const sortAndMergeUsers = (usersData: CustomUser[] | undefined, selectedUsers: CustomUser[]): CustomUser[] => {
  const users = usersData || [];
  const searchResultIds = new Set(users.map(u => u.id).filter(Boolean));

  const selectedUsersNotInResults = selectedUsers.filter(
    selected => selected.id && !searchResultIds.has(selected.id)
  );

  const allUsers = [...selectedUsersNotInResults, ...users];

  const uniqueUsers = Array.from(
    new Map(allUsers.map(user => [user.id, user])).values()
  );

  return uniqueUsers.sort((a, b) => {
    const aSelected = selectedUsers.some(selected => selected.id === a.id);
    const bSelected = selectedUsers.some(selected => selected.id === b.id);

    if (aSelected && !bSelected) {
      return -1;
    }
    if (!aSelected && bSelected) {
      return 1;
    }
    return 0;
  });
};

const filterUsersBySearch = (users: CustomUser[], searchInput: string): CustomUser[] => {
  const term = searchInput.trim().toLowerCase();
  if (!term) {
    return users;
  }

  return users.filter(user => {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return [fullName, user.email, user.username].some(value =>
      (value || '').toLowerCase().includes(term)
    );
  });
};

export interface UseModelFiltersProps {
  onFiltersChange: (filterState: {
    searchParam: string;
    searchStatus: string;
    fromDate: Date | null;
    toDate: Date | null;
    preparerIds: number[];
    reviewerIds: number[];
  }) => void;
  viewToApply?: {
    preparerIds: number[];
    reviewerIds: number[];
    searchStatus: string;
    fromDate: Date | string | null;
    toDate: Date | string | null;
  } | null;
}

export const useModelFilters = ({ onFiltersChange, viewToApply }: UseModelFiltersProps) => {
  const [searchParam, setSearchParam] = useState('');
  const [searchStatus, setSearchStatus] = useState('default');
  const [fromDueDate, setFromDueDate] = useState<Date | null>(null);
  const [toDueDate, setToDueDate] = useState<Date | null>(null);
  const [selectedPreparers, setSelectedPreparers] = useState<CustomUser[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<CustomUser[]>([]);
  const [preparerSearchInput, setPreparerSearchInput] = useState('');
  const [reviewerSearchInput, setReviewerSearchInput] = useState('');

  const debouncedSearchParam = useDebouncedValue(searchParam, 500);
  const lastAppliedViewRef = useRef<string | null>(null);

  // Fetch all staff users once and reuse for both preparer and reviewer selectors
  const { data: staffUsers = [], isLoading: isLoadingStaff } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return (response as CustomUser[]) || [];
    },
  });

  const sortedPreparers = useMemo(
    () => filterUsersBySearch(sortAndMergeUsers(staffUsers, selectedPreparers), preparerSearchInput),
    [staffUsers, selectedPreparers, preparerSearchInput]
  );

  const sortedReviewers = useMemo(
    () => filterUsersBySearch(sortAndMergeUsers(staffUsers, selectedReviewers), reviewerSearchInput),
    [staffUsers, selectedReviewers, reviewerSearchInput]
  );

  useEffect(() => {
    if (!viewToApply) {
      lastAppliedViewRef.current = null;
      return;
    }

    // Create a unique key for this view to prevent re-applying the same view
    // Sort arrays for comparison (numeric sort for IDs)
    const sortedPreparerIds = [...viewToApply.preparerIds].sort((a, b) => a - b);
    const sortedReviewerIds = [...viewToApply.reviewerIds].sort((a, b) => a - b);
    const viewKey = JSON.stringify({
      preparerIds: sortedPreparerIds,
      reviewerIds: sortedReviewerIds,
      searchStatus: viewToApply.searchStatus,
      fromDate: dateToISOStringKey(viewToApply.fromDate),
      toDate: dateToISOStringKey(viewToApply.toDate),
    });

    // Skip if we've already applied this exact view
    if (lastAppliedViewRef.current === viewKey) {
      return;
    }

    if (staffUsers.length > 0) {
      const preparersFromIds = viewToApply.preparerIds
        .map(id => staffUsers.find(user => user.id === id))
        .filter((user): user is CustomUser => user !== undefined);
      const reviewersFromIds = viewToApply.reviewerIds
        .map(id => staffUsers.find(user => user.id === id))
        .filter((user): user is CustomUser => user !== undefined);

      setSelectedPreparers(preparersFromIds);
      setSelectedReviewers(reviewersFromIds);
      lastAppliedViewRef.current = viewKey;
    }

    // Apply status from the view
    if (viewToApply.searchStatus) {
      setSearchStatus(viewToApply.searchStatus);
    }

    // Apply dates from the view
    if (viewToApply.fromDate !== undefined) {
      setFromDueDate(normalizeDate(viewToApply.fromDate));
    }
    if (viewToApply.toDate !== undefined) {
      setToDueDate(normalizeDate(viewToApply.toDate));
    }
  }, [viewToApply, staffUsers]);

  useEffect(() => {
    const preparerIds = selectedPreparers.map(user => user.id).filter((id): id is number => id !== undefined);
    const reviewerIds = selectedReviewers.map(user => user.id).filter((id): id is number => id !== undefined);

    onFiltersChange({
      searchParam: debouncedSearchParam,
      searchStatus,
      fromDate: fromDueDate,
      toDate: toDueDate,
      preparerIds,
      reviewerIds,
    });
  }, [
    debouncedSearchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    onFiltersChange,
  ]);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchStatusChange = (event: { target: { value: unknown } }) => {
    setSearchStatus(event.target.value as string);
  };

  const handleFromDueDateChange = (date: Date | null) => {
    setFromDueDate(date);
  };

  const handleToDueDateChange = (date: Date | null) => {
    if (fromDueDate && date && fromDueDate > date) {
      displayErrorMessage('From Valuation Date must be before To Valuation Date');
      return;
    }
    setToDueDate(date);
  };

  const handleUsersChange = (role: UserRole) => (_event: unknown, newValue: CustomUser[]) => {
    if (role === 'preparer') {
      setSelectedPreparers(newValue);
    } else {
      setSelectedReviewers(newValue);
    }
  };

  const cleanFilters = () => {
    setSearchParam('');
    setSearchStatus('default');
    setFromDueDate(null);
    setToDueDate(null);
    setSelectedPreparers([]);
    setSelectedReviewers([]);
    setPreparerSearchInput('');
    setReviewerSearchInput('');
  };

  return {
    // State
    searchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    sortedPreparers,
    sortedReviewers,
    isLoadingPreparers: isLoadingStaff,
    isLoadingReviewers: isLoadingStaff,
    // Handlers
    handleSearchParamChange,
    handleSearchStatusChange,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange: handleUsersChange('preparer'),
    handleReviewersChange: handleUsersChange('reviewer'),
    setPreparerSearchInput,
    setReviewerSearchInput,
    cleanFilters,
  };
};

